import React, { useState, useEffect } from 'react'
import { NavLink, Link, useNavigate } from 'react-router-dom'
import {
  SignOut,
  CaretDoubleLeft,
  CaretDoubleRight,
  Plus,
} from '@phosphor-icons/react'

import { useLang } from '../../context/LangContext'
import { useSite } from '../../context/SiteContext'
import { useAuth } from '../../context/AuthContext'

import LangToggle from '../LangToggle/LangToggle'
import sidebarMenu from './menu'

import icon_logo from '../../assets/images/logo.png'

export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const [collapsed, setCollapsed] = useState(false)
  const { t } = useLang()
  const { site } = useSite()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const isMobile = window.innerWidth <= 720;
  const unreadNotifications = 2;

  const handleLogout = () => {
    logout()
    // navigate('/select-site')
    navigate('/login', { replace: true })
  }

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 720) {
        setCollapsed(false);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={[
          'sidebar',
          sidebarOpen ? 'sidebar--open' : '',
          collapsed   ? 'sidebar--collapsed' : '',
        ].filter(Boolean).join(' ')}
      >
        {/* Header */}
        <div className="sidebar__header">
          {!collapsed && (
            <div className="sidebar__brand">
              <div className="sidebar__logo-icon">
                <img src={icon_logo} height={30} alt="Permata Suri logo" />
              </div>
              <div className="sidebar__brand-text">
                <span className="sidebar__brand-name">{site?.name ?? 'HelpDesk'}</span>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="sidebar__logo-icon sidebar__logo-icon--solo">
              <img src={icon_logo} height={30} alt="Permata Suri logo" />
            </div>
          )}
          <button
            className="sidebar__collapse-btn"
            onClick={() => {
              if (!isMobile) {
                setCollapsed((v) => !v);
              }
            }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed
              ? <CaretDoubleRight size={14} />
              : <CaretDoubleLeft size={14} />
            }
          </button>
        </div>

        {/* Site badge */}
        {!collapsed && site && false && (
          <div className="sidebar__site-badge">
            <span className={`sidebar__site-dot site-dot--${site.colorClass}`} />
            <span className="sidebar__site-name">{site.name}</span>
          </div>
        )}

        {/* New ticket shortcut */}
        <div className="sidebar__new-ticket">
          <Link
            to="/tickets/submit"
            className={`sidebar__new-btn ${collapsed ? 'sidebar__new-btn--icon' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <Plus size={15} />
            {!collapsed && <span>{t('newTicket')}</span>}
          </Link>
        </div>

        {/* Nav */}
        <nav className="sidebar__nav">
          {sidebarMenu.map((group) => (
            <div className="sidebar__group" key={group.titleKey}>
              {!collapsed && (
                <p className="sidebar__group-title">{t(group.titleKey) || group.titleKey}</p>
              )}
              {collapsed && <div className="sidebar__group-divider" />}
              <div className="sidebar__menu">
                {group.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === '/'}
                      className={({ isActive }) =>
                        ['sidebar__item', isActive ? 'sidebar__item--active' : ''].join(' ')
                      }
                      onClick={() => setSidebarOpen(false)}
                      title={collapsed ? t(item.labelKey) : undefined}
                    >
                      <div className="sidebar__item-left">
                        <div className="sidebar__icon-wrap">
                          <Icon size={18} className="sidebar__item-icon" />
                          {item.path === '/notifications' && unreadNotifications > 0 && (
                            <span className="sidebar__notif-dot" />
                          )}
                        </div>

                        {!collapsed && (
                          <span className="sidebar__item-label">{t(item.labelKey)}</span>
                        )}
                      </div>

                      {!collapsed && item.path === '/notifications' && unreadNotifications > 0 && (
                        <span className="sidebar__notif-badge">{unreadNotifications}</span>
                      )}
                    </NavLink>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        {!collapsed && (
          <div className="sidebar__lang">
            <LangToggle />
          </div>
        )}
        {/* Footer */}
        <div className="sidebar__footer">
          {!collapsed ? (
            <>
              <Link to="/profile" className="sidebar__profile">
                <div className="sidebar__user-info">
                  <span className="sidebar__name">{user?.name ?? 'User'}</span>
                  <span className="sidebar__email">{user?.email ?? ''}</span>
                </div>
              </Link>
              <button className="sidebar__logout" onClick={handleLogout}>
                <SignOut size={15} />
                <span>{t('signOut')}</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/profile" className="sidebar__avatar sidebar__avatar--center" title={user?.name}>
                {user?.initials ?? 'U'}
              </Link>
              <button
                className="sidebar__logout sidebar__logout--icon"
                title={t('signOut')}
                onClick={handleLogout}
              >
                <SignOut size={15} />
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  )
}
